import './lib/override';

import { createPinia, storeToRefs } from 'pinia';
import { createApp } from 'vue';
import Toast, { POSITION } from 'vue-toastification';
import { fetchEnv } from './config/fetch-env';
import { customQueryClient } from './config/query-client';
import { setDevUsers } from './config/set-dev-users';
import { launchCrowdinInContext } from '@/api/clients/crowdin/crowdin';
import vuetify from '@/config/vuetify';
import { initI18N } from '@/plugins/i18n/i18n';
import { useGlobalStore } from '@/stores/global';
import 'vue-toastification/dist/index.css';
import { Bugsnag, setIcons } from './config';
import { assetsPlugin, themePlugin, siteUrlPlugin, i18nPlugin, mixpanelPlugin } from './plugins';
import router from './router';
import App from './SuspensedApp.vue';
import * as LottiePlayer from '@lottiefiles/lottie-player';
import { VueQueryPlugin, type VueQueryPluginOptions } from '@tanstack/vue-query';
import { useFavicon } from '@vueuse/core';
import { createGtm } from '@gtm-support/vue-gtm';

const vueQueryPluginOptions: VueQueryPluginOptions = {
  queryClient: customQueryClient,
  enableDevtoolsV6Plugin: true,
};

const app = createApp(App);

/**
 * we need to create the pinia before fetching the config.json file
 * because config values are stored in the global store
 */
app.use(createPinia());

// Fetch the config.json file
fetchEnv()
  .then(async (config) => {
    if (!config) return;

    const globalStore = useGlobalStore();
    const icon = useFavicon();

    icon.value = `${config.assets_url}/img/favicon-2023.png`;
    globalStore.setConfig({
      ...config,
      apps_urls: {
        help: config.url_help,
        auth_url: config.url_auth,
        admin_account: config.url_admin_account,
        recruiter_account: config.url_recruiter_account,
        report_manager: config.url_report_manager,
        candidate_reports: config.url_candidate_reports,
        manager_account: config.url_manager_account,
      },
    });

    if (process.env.NODE_ENV === 'development') {
      await setDevUsers();
    }
  })
  .then(async () => {
    const { translation_url } = storeToRefs(useGlobalStore());

    const inContextDatas = launchCrowdinInContext();
    await initI18N(translation_url.value, inContextDatas);

    app.component('LottiePlayer', LottiePlayer);

    app.use(setIcons);
    app.use(VueQueryPlugin, vueQueryPluginOptions);
    app.use(Bugsnag);
    app.use(Toast, {
      timeout: 3000,
      position: POSITION.BOTTOM_CENTER,
      rtl: document.documentElement.dir === 'rtl',
    });
    app.use(assetsPlugin);
    app.use(i18nPlugin);
    app.use(vuetify);
    app.use(themePlugin);
    app.use(siteUrlPlugin);
    app.use(mixpanelPlugin);
    app.use(router);
    app.use(
      createGtm({
        id: 'GTM-5H4J27J',
        vueRouter: router,
      }),
    );

    app.mount('#app');
  });
